import { Component, inject, OnInit } from '@angular/core';
import Keycloak from 'keycloak-js';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService, NavigationItem, VerticalNavComponent, Role } from 'shared-lib';
import { RoleService } from '../auth/role-service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [VerticalNavComponent],
  template: '<lib-vertical-nav [environment]="environment" [navigationItems]="navigationItems" [showLanguageSwitcher]="true"></lib-vertical-nav>',
  styleUrl: './menu.component.scss'
})
export class MenuComponent implements OnInit {
  private readonly roleService = inject(RoleService);
  private readonly keycloak = inject(Keycloak);
  private readonly navigationService = inject(NavigationService);
  private readonly translate = inject(TranslateService);
  // Items keep their translation keys; navigationItems is the rendered, translated copy.
  private navKeyItems: NavigationItem[] = [];
  protected navigationItems: NavigationItem[] = [];
  protected readonly environment = environment;

  private async initializeNavigation(): Promise<void> {
    const navItems: NavigationItem[] = [];

    const isOrgMember = (await this.roleService.checkAccess(Role.OrganizationMember)) ||
      (await this.roleService.checkAccess(Role.OrganizationAdmin)) ||
      (await this.roleService.checkAccess(Role.Administrator));

    // Pools administer the organization, so they are admin-only and checked separately —
    // an OrganizationMember sees the Easypass entry but not this one.
    const isOrgAdmin = (await this.roleService.checkAccess(Role.OrganizationAdmin)) ||
      (await this.roleService.checkAccess(Role.Administrator));

    // These two were the wrong way round: Pools was offered to every member while the backend
    // has always required an admin for it (a member clicking it only ever met a 403), and
    // Easypass was hidden from members even though issuing one is exactly what they are for.
    if (isOrgAdmin) {
      navItems.push({ id: 'members', label: 'nav.people', icon: 'group', route: '/members' });
      navItems.push({ id: 'pools', label: 'nav.pools', icon: 'local_parking', route: '/pools' });
    }

    if (isOrgMember) {
      navItems.push({ id: 'easypasses', label: 'nav.easypass', icon: 'confirmation_number', route: '/easypasses' });
    }

    if (isOrgMember) {
      navItems.push({ id: 'my-account', label: 'nav.myAccount', icon: 'account_circle', route: '/my-account' });
    }

    if (this.roleService.isAuthenticated()) {
      navItems.push({
        id: 'logout',
        label: 'nav.logout',
        icon: 'logout',
        action: () => { this.keycloak.logout(); }
      });
    }

    this.navKeyItems = navItems;
    this.applyTranslations();
  }

  private applyTranslations(): void {
    this.navigationItems = this.navKeyItems.map(item => ({ ...item, label: this.translate.instant(item.label) }));
    this.navigationService.setNavigationItems(this.navigationItems);
  }

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(() => this.applyTranslations());
    this.initializeNavigation();
  }
}
